/**
 * Renders a data table inside TemplateWrapper and opens the browser print dialog,
 * matching the Facturiers list PDF flow (invoice template + window.print).
 * Uses Heritage invoice fonts & colors (Playfair / Cormorant / bronze palette).
 */

/** Heritage invoice template — same tokens as HeritageInvoicePreview tables */
const HERITAGE_INVOICE_THEME = {
  ink: '#3a3025',
  body: '#111111',
  muted: '#7d735f',
  accent: '#c7b299',
  border: '#e7e3da',
  rowBorder: '#f5f5f5',
  headerBg: '#fbfaf8',
  rowAlt: '#fafafa',
  fontTitle: '"Playfair Display", serif',
  fontBody: '"Cormorant Garamond", serif',
  fontArabic: '"Noto Sans Arabic", "Cormorant Garamond", "Segoe UI Historic", serif',
};

const HERITAGE_FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Noto+Sans+Arabic:wght@400;600;700&family=Playfair+Display:wght@400;600;700&display=swap';

/** Dedicated print CSS — zero page margins (Chrome « None ») + 100% width so Default also centers. */
const PRINT_CSS = `
  @page {
    size: A4 portrait;
    margin: 0 !important;
    margin-top: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
  }
  @page :first {
    margin: 0 !important;
  }
  *, *::before, *::after {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  html {
    width: 100%;
    height: auto;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff;
  }
  body {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    min-height: 100%;
    background: #fff !important;
    direction: rtl;
    font-family: ${HERITAGE_INVOICE_THEME.fontTitle};
    display: block;
  }
  .qawaem-print-root {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
  }
  .document-container,
  .document-container[style] {
    width: 100% !important;
    max-width: 100% !important;
    min-height: auto !important;
    margin: 0 auto !important;
    padding: 0 !important;
    gap: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    background: transparent !important;
    overflow: visible !important;
  }
  .invoice-a4-page,
  .invoice-a4-page[style] {
    width: 100% !important;
    max-width: 100% !important;
    min-height: 297mm !important;
    height: 297mm !important;
    max-height: 297mm !important;
    margin: 0 auto !important;
    padding: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    background: #fff !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    page-break-after: always !important;
    break-after: page !important;
    overflow: hidden !important;
  }
  .qawaem-list-export-pages > .invoice-a4-page:not(:last-child) {
    page-break-after: always !important;
    break-after: page !important;
  }
  .qawaem-list-export-pages > .invoice-a4-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }
  /* Legacy nested TemplateWrapper shells (flattened when possible) */
  .qawaem-list-export-pages > .document-container:not(:last-child) .invoice-a4-page,
  .qawaem-list-export-pages > .qawaem-list-export-page-sheet:not(:last-child) .invoice-a4-page {
    page-break-after: always !important;
    break-after: page !important;
  }
  .qawaem-list-export-pages > .document-container:last-child .invoice-a4-page,
  .qawaem-list-export-pages > .qawaem-list-export-page-sheet:last-child .invoice-a4-page {
    page-break-after: auto !important;
    break-after: auto !important;
  }
  .qawaem-list-export-table thead {
    display: table-header-group;
  }
  .qawaem-list-export-table tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .invoice-a4-page > div:first-of-type {
    width: calc(100% - 16mm) !important;
    max-width: calc(100% - 16mm) !important;
    margin: 8mm auto !important;
    box-sizing: border-box !important;
  }
  .heritage-invoice-preview,
  .heritage-invoice-container {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
  }
  .invoice-document-footer {
    width: 100% !important;
    padding-left: 8mm !important;
    padding-right: 8mm !important;
    text-align: center !important;
    direction: rtl !important;
    box-sizing: border-box !important;
  }
  .qawaem-list-export-root {
    direction: rtl !important;
    text-align: right !important;
    width: 100% !important;
    font-family: ${HERITAGE_INVOICE_THEME.fontBody};
    color: ${HERITAGE_INVOICE_THEME.body};
  }
  .qawaem-list-export-section-title {
    font-family: ${HERITAGE_INVOICE_THEME.fontTitle} !important;
    color: ${HERITAGE_INVOICE_THEME.ink} !important;
    border-bottom: 1px solid ${HERITAGE_INVOICE_THEME.accent} !important;
  }
  .qawaem-list-export-table th {
    background: ${HERITAGE_INVOICE_THEME.headerBg} !important;
    color: ${HERITAGE_INVOICE_THEME.ink} !important;
    border-bottom: 1px solid ${HERITAGE_INVOICE_THEME.border} !important;
    font-family: ${HERITAGE_INVOICE_THEME.fontTitle} !important;
    font-weight: 600 !important;
  }
  .qawaem-list-export-table td {
    color: ${HERITAGE_INVOICE_THEME.body} !important;
    border-bottom: 1px solid ${HERITAGE_INVOICE_THEME.rowBorder} !important;
    font-family: ${HERITAGE_INVOICE_THEME.fontBody} !important;
  }
  div[style*="left: -9999px"],
  div[style*="left:-9999px"] {
    display: none !important;
  }
  @media print {
    @page {
      size: A4 portrait;
      margin: 0 !important;
    }
    html, body {
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    .qawaem-print-root,
    .document-container,
    .invoice-a4-page {
      width: 100% !important;
      max-width: 100% !important;
      margin: 0 auto !important;
    }
  }
`;

/** Inline script: reinforce zero margins right before Chrome opens print dialog. */
const PRINT_SCRIPT = `
(function () {
  function applyZeroMargins() {
    var id = 'qawaem-print-zero-margin';
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement('style');
      el.id = id;
      el.setAttribute('media', 'print');
      document.head.appendChild(el);
    }
    el.textContent = '@page { size: A4 portrait; margin: 0 !important; }';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
  }
  window.addEventListener('beforeprint', applyZeroMargins);
  window.onload = function () {
    applyZeroMargins();
    setTimeout(function () {
      applyZeroMargins();
      window.print();
    }, 650);
  };
})();
`;

/** Rows per A4 page — tuned for Heritage header + footer (fast, no DOM probing). */
const LIST_EXPORT_FIRST_PAGE_ROWS = 26;
const LIST_EXPORT_CONTINUATION_PAGE_ROWS = 32;

function paginateListExportRows(rows, firstPageRows, continuationRows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [[]];
  const firstCap = Math.max(1, firstPageRows || LIST_EXPORT_FIRST_PAGE_ROWS);
  const nextCap = Math.max(1, continuationRows || LIST_EXPORT_CONTINUATION_PAGE_ROWS);
  if (list.length <= firstCap) return [list];
  const pages = [list.slice(0, firstCap)];
  for (let i = firstCap; i < list.length; i += nextCap) {
    pages.push(list.slice(i, i + nextCap));
  }
  const exported = pages.reduce((sum, page) => sum + page.length, 0);
  if (exported !== list.length) {
    const safeFirst = Math.min(LIST_EXPORT_FIRST_PAGE_ROWS, list.length);
    const fallbackPages = [list.slice(0, safeFirst)];
    for (let i = safeFirst; i < list.length; i += LIST_EXPORT_CONTINUATION_PAGE_ROWS) {
      fallbackPages.push(list.slice(i, i + LIST_EXPORT_CONTINUATION_PAGE_ROWS));
    }
    return fallbackPages;
  }
  return pages;
}

/** Ensure long lists always spill to page 2+ — never trust "all rows fit page 1" alone. */
function buildListExportRowPages(rows, firstPageRows, continuationRows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return [[]];

  let first = Math.max(1, Number(firstPageRows) || LIST_EXPORT_FIRST_PAGE_ROWS);
  const next = Math.max(1, Number(continuationRows) || LIST_EXPORT_CONTINUATION_PAGE_ROWS);

  if (list.length > 1 && first >= list.length) {
    first = list.length - 1;
  }

  let pages = paginateListExportRows(list, first, next);
  const exported = pages.reduce((sum, page) => sum + page.length, 0);

  if (exported !== list.length) {
    first = Math.min(LIST_EXPORT_FIRST_PAGE_ROWS, Math.max(1, list.length - 1));
    pages = paginateListExportRows(list, first, next);
  }

  return pages;
}

function flattenListExportPrintHtml(printRoot) {
  if (!printRoot) return '';
  const pagesHost = printRoot.querySelector('.qawaem-list-export-pages');
  if (!pagesHost) return printRoot.innerHTML;

  const sheets = pagesHost.querySelectorAll(':scope > .invoice-a4-page, :scope > .document-container .invoice-a4-page, :scope > .qawaem-list-export-page-sheet .invoice-a4-page');
  if (!sheets.length) return printRoot.innerHTML;

  const flatPages = Array.from(sheets).map((page) => page.outerHTML).join('');
  return `<div class="document-container qawaem-list-export-pages">${flatPages}</div>`;
}

function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function printListPdfWithInvoiceTemplate({
  settings,
  rows = [],
  columns = [],
  listExportTitle,
  clientTitle,
  sectionTitle = 'البيانات المُصدَّرة',
  printDocumentTitle = 'تصدير',
  renderDelayMs = 700,
  onComplete,
}) {
  const React = await import('react');
  const ReactDOM = await import('react-dom/client');
  const { default: TemplateWrapper } = await import('../invoice/templates/TemplateWrapper');
  const { SettingsProvider } = await import('../../../core/contexts/SettingsContext');
  const { ClientsProvider } = await import('../../../core/contexts/ClientsContext');
  const { ProductsProvider } = await import('../../../core/contexts/ProductsContext');

  const dateStr = new Date().toISOString().split('T')[0];
  const summaryLine = `الإجمالي: ${rows.length} سجل — تم الإنشاء في ${new Date().toLocaleDateString('ar-SA')}`;

  const sectionTitleStyle = {
    fontSize: '13px',
    fontWeight: 700,
    marginBottom: '10px',
    color: HERITAGE_INVOICE_THEME.ink,
    fontFamily: HERITAGE_INVOICE_THEME.fontTitle,
    textAlign: 'right',
    paddingBottom: '8px',
    borderBottom: `1px solid ${HERITAGE_INVOICE_THEME.accent}`,
    display: 'block',
  };

  const thStyle = {
    textAlign: 'right',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 600,
    color: HERITAGE_INVOICE_THEME.ink,
    borderBottom: `1px solid ${HERITAGE_INVOICE_THEME.border}`,
    fontFamily: HERITAGE_INVOICE_THEME.fontTitle,
    backgroundColor: HERITAGE_INVOICE_THEME.headerBg,
    whiteSpace: 'nowrap',
  };

  const tdStyle = {
    padding: '5px 8px',
    fontSize: '12px',
    color: HERITAGE_INVOICE_THEME.body,
    borderBottom: `1px solid ${HERITAGE_INVOICE_THEME.rowBorder}`,
    fontFamily: HERITAGE_INVOICE_THEME.fontBody,
    fontWeight: 500,
    textAlign: 'right',
    verticalAlign: 'top',
  };

  const formatCell = (raw) => {
    if (raw === null || raw === undefined || raw === '') return '—';
    return String(raw);
  };

  const buildPageTableContent = (pageRows, pageIdx, totalPages, globalRowOffset) =>
    React.createElement(
      'div',
      {
        className: 'qawaem-list-export-root',
        style: { width: '100%', direction: 'rtl', textAlign: 'right' },
      },
      pageIdx === 0
        ? React.createElement('div', { className: 'qawaem-list-export-section-title', style: sectionTitleStyle }, sectionTitle)
        : null,
      totalPages > 1
        ? React.createElement(
            'div',
            {
              style: {
                fontSize: '11px',
                color: HERITAGE_INVOICE_THEME.muted,
                marginBottom: '8px',
                fontFamily: HERITAGE_INVOICE_THEME.fontBody,
                textAlign: 'right',
              },
            },
            `صفحة ${pageIdx + 1} من ${totalPages}`,
          )
        : null,
      React.createElement(
        'table',
        {
          className: 'qawaem-list-export-table',
          style: { width: '100%', borderCollapse: 'collapse', direction: 'rtl', tableLayout: 'auto' },
        },
        React.createElement(
          'thead',
          null,
          React.createElement(
            'tr',
            null,
            columns.map((col, i) => React.createElement('th', { key: i, style: thStyle }, col.label)),
          ),
        ),
        React.createElement(
          'tbody',
          null,
          pageRows.map((row, rowIdx) => {
            const globalIdx = globalRowOffset + rowIdx;
            return React.createElement(
              'tr',
              {
                key: row.id != null ? `${row.id}-${globalIdx}` : globalIdx,
                style: { backgroundColor: globalIdx % 2 === 1 ? HERITAGE_INVOICE_THEME.rowAlt : '#ffffff' },
              },
              columns.map((col, colIdx) => {
                const raw = col.value(row, globalIdx);
                const text = formatCell(raw);
                const isNumeric = col.align === 'right' || col.numeric;
                return React.createElement('td', {
                  key: colIdx,
                  style: isNumeric
                    ? { ...tdStyle, textAlign: 'left', direction: 'ltr', unicodeBidi: 'plaintext' }
                    : tdStyle,
                }, text);
              }),
            );
          }),
        ),
      ),
    );

  const formData = {
    number: `EXP-${dateStr.replace(/-/g, '')}`,
    date: dateStr,
    items: [],
  };

  const clientData = {
    name: clientTitle,
    address: summaryLine,
  };

  const rowPages = buildListExportRowPages(rows, LIST_EXPORT_FIRST_PAGE_ROWS, LIST_EXPORT_CONTINUATION_PAGE_ROWS);
  const totalPages = rowPages.length;
  let globalRowOffset = 0;

  const pageWrappers = rowPages.map((pageRows, pageIdx) => {
    const wrapper = React.createElement(TemplateWrapper, {
      key: `list-export-page-${pageIdx}`,
      documentType: 'invoice',
      formData,
      clientData,
      partnerType: 'client',
      paperSize: 'A4',
      settings,
      listExportTitle,
      dataListExportContent: buildPageTableContent(pageRows, pageIdx, totalPages, globalRowOffset),
      isPrintExport: true,
      listExportEmbedMode: true,
      listExportContinuation: pageIdx > 0,
      listExportPageIndex: pageIdx,
      listExportPageCount: totalPages,
    });
    globalRowOffset += pageRows.length;
    return wrapper;
  });

  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;left:-9999px;top:0;width:210mm;visibility:hidden;pointer-events:none;';
  container.innerHTML = `<link rel="stylesheet" href="${HERITAGE_FONT_LINK}"><style>${PRINT_CSS}</style><div class="qawaem-print-root"></div>`;
  document.body.appendChild(container);

  const root = ReactDOM.createRoot(container.querySelector('.qawaem-print-root'));
  root.render(
    React.createElement(
      SettingsProvider,
      null,
      React.createElement(
        ClientsProvider,
        null,
        React.createElement(
          ProductsProvider,
          null,
          React.createElement(
            'div',
            {
              className: 'document-container qawaem-list-export-pages',
              style: { display: 'flex', flexDirection: 'column', gap: 0, alignItems: 'stretch' },
            },
            ...pageWrappers,
          ),
        ),
      ),
    ),
  );

  await waitMs(renderDelayMs + Math.max(0, totalPages - 1) * 120);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const embeddedLogo = await (async () => {
    const { embedImageAsDataUrl, resolveCompanyLogoSrc } = await import('../../../shared/utils/companyLogo');
    return embedImageAsDataUrl(resolveCompanyLogoSrc(settings?.company?.logo, origin));
  })();

  // Drop embedded <style> blocks (TemplateWrapper @page margins broke centering).
  const printRoot = container.querySelector('.qawaem-print-root');
  let templateHTML = flattenListExportPrintHtml(printRoot).replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  if (!templateHTML.trim() || !printRoot?.querySelector('.invoice-a4-page')) {
    root.unmount();
    document.body.removeChild(container);
    throw new Error('PDF export render failed — no pages generated');
  }
  if (embeddedLogo) {
    const { absolutizeImgSrcInHtml } = await import('../../../shared/utils/companyLogo');
    templateHTML = absolutizeImgSrcInHtml(templateHTML, origin, embeddedLogo);
  }
  const safeTitle = printDocumentTitle.replace(/</g, '');
  const printDialogDelayMs = 400 + Math.max(0, totalPages - 1) * 100;
  const printScript = PRINT_SCRIPT.replace('650', String(printDialogDelayMs));

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    root.unmount();
    document.body.removeChild(container);
    throw new Error('POPUP_BLOCKED');
  }

  const printHTML = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${HERITAGE_FONT_LINK}" rel="stylesheet">
  <style>${PRINT_CSS}</style>
</head>
<body>
  <div class="qawaem-print-root">${templateHTML}</div>
  <script>${printScript}<\/script>
</body>
</html>`;
  printWindow.document.write(printHTML);
  printWindow.document.close();
  printWindow.focus();

  root.unmount();
  document.body.removeChild(container);
  if (typeof onComplete === 'function') onComplete();
}
